import {Injectable} from '@angular/core';
import {RequestService} from "../request/request.service";
import {UserService} from "../user/user.service";
import {UtilService} from "../util/util.service";
import {CatalogDataModel} from "./catalog-data.model";
import {CatalogQuestionModel} from "./catalog-question.model";
import {LegacyInitializer} from "../initialization/legacy.initializer";
import {CatalogQuestionResponseModel} from "./catalog-question-response.model";
import {ReplaySubject} from "rxjs";
import {CatalogSubmitRequestResponse} from "./catalog-submit-request-response";

@Injectable({
  providedIn: 'root'
})
export class CatalogDataService {

  private currentCatalogRequestId = null;

  private catalog: CatalogDataModel;

  private highlightedItems: CatalogDataModel[] = [];

  private categoryIdToModel: {
    [key: string]: CatalogDataModel
  } = {};

  private categoryKeyToModel: {
    [key: string]: CatalogDataModel
  } = {};

  private categoryNameToModel: {
    [key: string]: CatalogDataModel
  } = {};

  private itemIdToModel: {
    [key: string]: CatalogDataModel
  } = {};

  private itemKeyToModel: {
    [key: string]: CatalogDataModel
  } = {};

  private itemNameToModel: {
    [key: string]: CatalogDataModel
  } = {};

  catalogLoaded: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(private requestService: RequestService,
              private userService: UserService,
              private utilService: UtilService,
              private legacyInitializer: LegacyInitializer) {
      this.init(true);

    (window as any).NG_SHARED_CATALOG_DATA_SERVICE = this;

    (window as any).WebsocketAPI.registerAPIImplementation("catalogContent", (data: any, responseBody: any) => {
      this.catalog = null;
      this.highlightedItems = [];
      this.categoryIdToModel = {};
      this.itemNameToModel = {};
      this.categoryNameToModel = {};
      this.itemNameToModel = {};
      this.init(false);
    });
  }

  async init(firstTime: boolean) {
    if (this.userService.isAuthenticated()) {
      try {
        const response: any = await this.requestService.createRequest()
          .controller("servicely.core.Portal")
          .method("getCatalogData")
          .execute();

        const data: { categories: CatalogDataModel[], items: CatalogDataModel[], highlightedItems: CatalogDataModel[] } = JSON.parse(response.data);

        let rootCategory: CatalogDataModel;

        const parentlessCategories: CatalogDataModel[] = data.categories.filter((category) => {
          return category.parentId.length === 0;
        });

        // If there is more than one parentless category (or no categories) then we synthesise a root category.
        if (parentlessCategories.length > 1 || data.categories.length === 0) {
          const syntheticId = "__synthetic_root__";
          const root: CatalogDataModel = {
            type: "category",
            name: "Catalog",
            description: "Synthesised root of the category tree",
            id: syntheticId,
            parentId: "",
            categories: parentlessCategories
          };

          parentlessCategories.forEach((category) => {
            category.parentId = syntheticId;
          });

          data.categories.push(root);
          rootCategory = root;
        } else if (parentlessCategories.length == 1) {
          rootCategory = parentlessCategories[0];
        } else {
          const msg = "Every category has a parent, therefore there is a loop in the catalog";
          alert(msg);
          throw new Error(msg);
        }

        data.categories.forEach((category) => {
          this.categoryIdToModel[category.id] = category;
          if (category.key) {
            this.categoryKeyToModel[category.key] = category;
          }
          this.categoryNameToModel[category.name.toLowerCase()] = category;
          category.categories = [];
          category.items = [];
        });

        data.categories.forEach((category) => {
          if (category.parentId) {
            this.categoryIdToModel[category.parentId].categories.push(category);
            category.parent = this.categoryIdToModel[category.parentId];
          }
          category.type = "category";
        });

        // Put unordered categories at the end.
        data.categories.forEach((category) => {
          if (!category.order) {
            category.order = 10000;
          } else {
            category.order = Number(category.order);
          }
        });

        // Order all the categories
        data.categories.forEach((category) => {
          category.categories.sort((a: CatalogDataModel, b: CatalogDataModel) => {
            return a.order - b.order;
          });
        });

        this.assignCategoryLevel(rootCategory, 0);

        this.catalog = rootCategory;

        // Highlighted items
        this.highlightedItems = data.highlightedItems || [];

        // Order all the highlighted items
        this.highlightedItems.sort((a, b) => {
          return a.order - b.order;
        });

        //
        // TODO we might lazy load the items as the category is loaded
        //

        data.items.forEach((item) => {
          this.itemIdToModel[item.id] = item;
          if (item.key) {
            this.itemKeyToModel[item.key] = item;
          }
          this.itemNameToModel[item.name.toLowerCase()] = item;
          if (item.parentId) {
            this.categoryIdToModel[item.parentId].items.push(item);
            item.parent = this.categoryIdToModel[item.parentId];
          }
          item.type = "item";

          if (item.highlight && !this.highlightedItems.find((hi => hi.id == item.id))) {
            this.highlightedItems.push(item);
          }
        });

        // Put unordered items at the end.
        data.items.forEach((item) => {
          if (!item.order) {
            item.order = 10000;
          } else {
            item.order = Number(item.order);
          }
        });

        // Order all the items
        data.categories.forEach((category) => {
          category.items.sort((a: CatalogDataModel, b: CatalogDataModel) => {
            return a.order - b.order;
          });
        });

        this.assignItemLevel(rootCategory, 0);

        this.catalogLoaded.next(firstTime);

      } catch (e) {
        const errorMessage = e.responseJSON ? e.responseJSON.errors.join("\n") : e.toString();
        console.error("We encountered an error when loading the catalog:\n" + errorMessage, e);
      }
    }
  }

  getCatalog(): CatalogDataModel {
      return this.catalog;
  }

  getHighlightedItems(): CatalogDataModel[] {
    return this.highlightedItems;
  }

  async getAllQuestions(id: string) {
    const parameters = {
      id: id,
    };

    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method("getAllQuestions")
      .data(parameters)
      .execute();

    const data: { questions: CatalogQuestionModel[] } = JSON.parse(response.data);

    return data.questions;
  }

  async getAllAnswers(id: string, table: string) {
    const parameters = {
      relatedRecordId: id,
      relatedRecordTable: table,
    };

    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method("getQuestionsForRelatedRecord")
      .data(parameters)
      .execute();

    return response.data;
  }

  /**
   * Gets the first set of Questions. If initialRecordID is presented, it will be used for the new/updated record.
   * If initialRecordID is null, new ID/record will be generated.
   *
   * @param itemId
   * @param nluModel
   * @param initialRecordID
   */
  async getFirstQuestions(itemId: string, nluModel: any, initialRecordID: any, sourceRec: any, sourceIsNewRecord: boolean): Promise<CatalogQuestionResponseModel> {
    const parameters = {
      id: itemId,
      nlu_match: nluModel,
      initialRecordID: initialRecordID,
      sourceRec: sourceRec,
      sourceIsNewRecord: sourceIsNewRecord
    };

    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method("getFirstQuestions")
      .data(parameters)
      .execute();

    return response.data;
  }

  async getNextQuestions(itemId: string, requestId: string, precedingQuestionId: string, triggeredQuestionId: string, answers: any, nluModel: any, sourceRec: any, sourceIsNewRecord: boolean): Promise<CatalogQuestionResponseModel> {

    const stringifiedAnswers = {};

    Object.getOwnPropertyNames(answers).forEach((answerKey) => {
      let answer = answers[answerKey];

      if (answer == null || answer == undefined) {
        stringifiedAnswers[answerKey] = null;
      } else {
        // Case for reference fields
        if (answer.hasOwnProperty("id")) {
          stringifiedAnswers[answerKey] = answer["id"];
        } else {
          stringifiedAnswers[answerKey] = answers[answerKey].toString();
        }
      }
    });

    const parameters = {
      id: itemId,
      requestId: requestId,
      precedingQuestionId: precedingQuestionId,
      triggeredQuestionId: triggeredQuestionId,
      answers: stringifiedAnswers,
      nlu_match: nluModel,
      sourceRec: sourceRec,
      sourceIsNewRecord: sourceIsNewRecord
    };

    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method("getNextQuestions")
      .data(parameters)
      .execute();

    return response.data;
  }

  async submitRequest(itemId: string, requestId: string, sourceRec: any, sourceIsNewRecord: boolean): Promise<CatalogSubmitRequestResponse> {

    const parameters = {
      id: itemId,
      requestId: requestId,
      sourceRec: sourceRec,
      sourceIsNewRecord: sourceIsNewRecord
    };

    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method("submitRequest")
      .data(parameters)
      .execute();

    return response.data;
  }

  async sendAnswer(itemId: string, requestId: string, precedingQuestionId: string, triggeredQuestionId: string, answers: any, deleteAnswers: boolean = false) {
    const stringifiedAnswers = {};

    Object.getOwnPropertyNames(answers).forEach((answerKey) => {
      let answer = answers[answerKey];

      if (answer == null || answer == undefined) {
        stringifiedAnswers[answerKey] = null;
      } else {
        // Case for reference fields
        if (answer.hasOwnProperty("id")) {
          stringifiedAnswers[answerKey] = answer["id"];
        } else {
          stringifiedAnswers[answerKey] = answers[answerKey].toString();
        }
      }
    });

    const parameters = {
      id: itemId,
      requestId: requestId,
      precedingQuestionId: precedingQuestionId,
      triggeredQuestionId: triggeredQuestionId,
      answers: stringifiedAnswers,
      deleteAnswers: deleteAnswers
    };

    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method("sendAnswer")
      .data(parameters)
      .execute();

    return response.data;
  }

  findCategoryByName(name: string): CatalogDataModel {
    return this.categoryNameToModel[name.toLowerCase()];
  }

  findItemByName(name: string): CatalogDataModel {
    return this.itemNameToModel[name.toLowerCase()];
  }

  findCategoryById(id: string): CatalogDataModel {
    return this.categoryIdToModel[id];
  }

  findItemById(id: string): CatalogDataModel {
    return this.itemIdToModel[id];
  }

  findCategoryByKey(key: string): CatalogDataModel {
    return this.categoryKeyToModel[key];
  }

  findItemByKey(key: string): CatalogDataModel {
    return this.itemKeyToModel[key];
  }

  private assignCategoryLevel(node: CatalogDataModel, level: number) {
    node.level = level;
    node.categories.forEach((category) => {
      this.assignCategoryLevel(category, level + 1);
    });
  };

  private assignItemLevel(node: CatalogDataModel, level: number) {
    node.items.forEach((item) => {
      item.level = level;
    });
    node.categories.forEach((category) => {
      this.assignItemLevel(category, level + 1);
    });
  };

  setCurrentCatalogRequestId(requestId: String) {
    this.currentCatalogRequestId = requestId;
  }

  getCurrentCatalogRequestId() {
    return this.currentCatalogRequestId;
  }

  async getQuestionItemById(id: string) {
    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method("getQuestionItemByID")
      .data({ id: id })
      .execute();

    return response.data ? JSON.parse(response.data) : null;
  }

  async getQuestionItemByKey(key: string) {
    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method("getQuestionItemByKey")
      .data({ key: key })
      .execute();

    return response.data ? JSON.parse(response.data) : null;
  }
}
